import { validateStandardSchema } from '@angular/forms/signals';

import type { LineEntity } from '@sites/entity-core';
import { DefaultLineEntitySchema } from '@sites/entity-core';

import type { BaseEntityFormOptions } from '../../core/create-base-entity-form';
import { configureVersionedReadonly, createBaseEntityForm } from '../../core/create-base-entity-form';
import { composeForm } from '../../core/compose-form';
import { widgetEnvelopePlugin } from '../../plugins/widget/widget-envelope.plugin';

export type LineFormModel = LineEntity;
export type LinePropsModel = LineEntity['props'];

type Desktop<K extends keyof LinePropsModel> = LinePropsModel[K]['desktop'];

/**
 * Signal-form view of the line (divider) widget at its latest version, over the versioned base
 * form. Single placement, single factory.
 *
 * The accessors keep the legacy field names and expose each prop's DESKTOP
 * leaf — the only breakpoint the panel edits. The `mobile` leaves migrated from
 * a persisted `mobileStyles` bag ride along untouched (reachable through
 * `_raw.props.<prop>.mobile`).
 */
export function createLineForm(options?: BaseEntityFormOptions<LineEntity>) {
    const base = createBaseEntityForm<LineEntity>((schemaPath) => {
        validateStandardSchema(schemaPath, DefaultLineEntitySchema.latestSchema);
        configureVersionedReadonly(schemaPath);
    }, options);

    const _raw = base._raw;

    return composeForm(
        base,
        widgetEnvelopePlugin(() => _raw),
        {
            get horizontalAlign() {
                return _raw.props.horizontalAlign.desktop;
            },
            setHorizontalAlign: (value: Desktop<'horizontalAlign'>) => {
                _raw.props.horizontalAlign.desktop().value.set(value);
            },

            get borderTopColor() {
                return _raw.props.borderTopColor.desktop;
            },
            setBorderTopColor: (value: Desktop<'borderTopColor'>) => {
                _raw.props.borderTopColor.desktop().value.set(value);
            },
            get borderTopWidth() {
                return _raw.props.borderTopWidth.desktop;
            },
            setBorderTopWidth: (value: Desktop<'borderTopWidth'>) => {
                _raw.props.borderTopWidth.desktop().value.set(value);
            },
            get borderTopStyle() {
                return _raw.props.borderTopStyle.desktop;
            },
            setBorderTopStyle: (value: Desktop<'borderTopStyle'>) => {
                _raw.props.borderTopStyle.desktop().value.set(value);
            },
            /** The divider's own width, as a PERCENTAGE of the column (0–100). */
            get width() {
                return _raw.props.width.desktop;
            },
            setWidth: (value: Desktop<'width'>) => {
                _raw.props.width.desktop().value.set(value);
            },
            /** Wrapper margin shorthand — carried over from legacy, never edited in the UI. */
            get margin() {
                return _raw.props.margin.desktop;
            },
        }
    );
}

export type LineForm = ReturnType<typeof createLineForm>;
